import { supabaseRestRequest } from './familyService';
import { sosGroups, type SOSDisease, type SOSOption } from '../utils/sosData';
import { supabase } from '../lib/supabase';

interface RawSOSDisease {
  id: string;
  name: string;
  description: string | null;
  tipo_caracteristica: string[] | null;
  piora_com: string[] | null;
  melhora_com: string[] | null;
  causas_sensacoes: string[] | null;
  sintomas_concomitantes: string[] | null;
  comportamentos_reacoes: string[] | null;
  temporalidade_ritmo: string[] | null;
  selection_limits: Record<string, number> | null;
  knowledge_base_file: string | null;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface DiseasePayload {
  name: string;
  description?: string;
  active: boolean;
  groups: SOSDisease['groups'];
  selectionLimits?: SOSDisease['selectionLimits'];
  knowledgeBaseFile?: File;
}

const GROUP_IDS = sosGroups.map((group) => group.id);

const createOptionId = (label: string) =>
  `option_${label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${Date.now()}`;

const parseOption = (value: string, index: number): SOSOption => {
  try {
    const parsed = JSON.parse(value) as SOSOption;
    if (parsed && parsed.id && parsed.label) {
      return parsed;
    }
  } catch {
    // ignore parse errors and fallback to plain string
  }

  const label = value.trim();
  return {
    id: createOptionId(`${label}_${index}`),
    label,
  };
};

const serializeOptions = (options: SOSOption[] = []): string[] =>
  options.map((option) => JSON.stringify(option));

const mapRawToDisease = (raw: RawSOSDisease): SOSDisease => {
  const groups: SOSDisease['groups'] = {};

  const rawGroups: Record<string, string[] | null> = {
    tipo_caracteristica: raw.tipo_caracteristica,
    piora_com: raw.piora_com,
    melhora_com: raw.melhora_com,
    causas_sensacoes: raw.causas_sensacoes,
    sintomas_concomitantes: raw.sintomas_concomitantes,
    comportamentos_reacoes: raw.comportamentos_reacoes,
    temporalidade_ritmo: raw.temporalidade_ritmo,
  };

  for (const groupId of GROUP_IDS) {
    const values = rawGroups[groupId] || [];
    groups[groupId] = values.map((value, index) => parseOption(value, index));
  }

  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? undefined,
    active: raw.active,
    groups,
    selectionLimits: raw.selection_limits ?? undefined,
    knowledge_base_file: raw.knowledge_base_file ?? undefined,
  };
};

const buildPayload = (payload: DiseasePayload, userId: string, knowledgeBaseFileName?: string) => {
  const base: Record<string, unknown> = {
    name: payload.name,
    description: payload.description ?? null,
    active: payload.active,
    created_by: userId,
    selection_limits: payload.selectionLimits ?? null,
    knowledge_base_file: knowledgeBaseFileName ?? null,
  };

  for (const groupId of GROUP_IDS) {
    base[groupId] = serializeOptions(payload.groups[groupId] || []);
  }

  return base;
};

const buildUpdatePayload = (payload: Partial<DiseasePayload>, knowledgeBaseFileName?: string) => {
  const base: Record<string, unknown> = {};

  if (payload.name !== undefined) base.name = payload.name;
  if (payload.description !== undefined) base.description = payload.description ?? null;
  if (payload.active !== undefined) base.active = payload.active;
  if (payload.selectionLimits !== undefined) base.selection_limits = payload.selectionLimits ?? null;
  if (knowledgeBaseFileName !== undefined) base.knowledge_base_file = knowledgeBaseFileName;

  if (payload.groups) {
    for (const groupId of GROUP_IDS) {
      if (payload.groups[groupId] !== undefined) {
        base[groupId] = serializeOptions(payload.groups[groupId] || []);
      }
    }
  }

  return base;
};

export const getCurrentUserId = (): string => {
  const tokensStr = localStorage.getItem('supabase.auth.token');
  if (!tokensStr) {
    throw new Error('Usuário não autenticado. Faça login novamente.');
  }

  try {
    const tokens = JSON.parse(tokensStr);
    const accessToken: string | undefined = tokens?.access_token;
    if (!accessToken) {
      throw new Error('Token de acesso inválido.');
    }

    const payload = JSON.parse(atob(accessToken.split('.')[1])) as { sub?: string };
    if (!payload?.sub) {
      throw new Error('Token de acesso sem usuário válido.');
    }

    return payload.sub;
  } catch (error) {
    console.error('Erro ao obter user id do token', error);
    throw new Error('Não foi possível identificar o usuário autenticado.');
  }
};

class SOSService {
  private static instance: SOSService;

  private constructor() {}

  static getInstance(): SOSService {
    if (!SOSService.instance) {
      SOSService.instance = new SOSService();
    }
    return SOSService.instance;
  }

  private async uploadKnowledgeBaseFile(file: File, diseaseName: string): Promise<string> {
    const fileName = `${diseaseName.toLowerCase().replace(/\s+/g, '-')}.txt`;
    
    const { error } = await supabase.storage
      .from('sos-knowledge')
      .upload(fileName, file, {
        contentType: 'text/plain',
        upsert: true
      });

    if (error) {
      throw new Error(`Erro ao fazer upload da base de conhecimento: ${error.message}`);
    }

    return fileName;
  }

  async getDiseases(): Promise<SOSDisease[]> {
    const data = await supabaseRestRequest('sos_diseases?select=*&order=name.asc', 'GET');
    return (Array.isArray(data) ? data : []).map(mapRawToDisease);
  }

  async createDisease(payload: DiseasePayload): Promise<SOSDisease> {
    const userId = getCurrentUserId();
    
    let knowledgeBaseFileName: string | undefined;
    if (payload.knowledgeBaseFile) {
      knowledgeBaseFileName = await this.uploadKnowledgeBaseFile(payload.knowledgeBaseFile, payload.name);
    }
    
    const body = buildPayload(payload, userId, knowledgeBaseFileName);
    const data = await supabaseRestRequest('sos_diseases?select=*', 'POST', body);
    const created = Array.isArray(data) ? data[0] : data;
    return mapRawToDisease(created);
  }

  async updateDisease(id: string, payload: Partial<DiseasePayload>): Promise<SOSDisease> {
    let knowledgeBaseFileName: string | undefined;
    if (payload.knowledgeBaseFile && payload.name) {
      knowledgeBaseFileName = await this.uploadKnowledgeBaseFile(payload.knowledgeBaseFile, payload.name);
    }
    
    const body = buildUpdatePayload(payload, knowledgeBaseFileName);
    const data = await supabaseRestRequest(`sos_diseases?id=eq.${id}&select=*`, 'PATCH', body);
    const updated = Array.isArray(data) ? data[0] : data;
    return mapRawToDisease(updated);
  }

  async deleteDisease(id: string): Promise<void> {
    await supabaseRestRequest(`sos_diseases?id=eq.${id}`, 'DELETE');
  }
}

export const sosService = SOSService.getInstance();
export type { SOSDisease, SOSOption };
